import {
  Alert,
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNotify, usePermissions, useTranslate } from 'react-admin';
import {
  BB_LEADERBOARD_FLAG,
  BbLbBoardCatalogEntry,
  BbLbPhase,
  bbGetFeatureFlags,
  bbSetFeatureFlag,
  bbLbGetBoardCatalog,
  bbLbGetPhases,
} from '../../../services/blindBoxService';
import { PermissionData } from '../../../entities/permission.entity';
import { hasPermission } from '../../../utils/has-permission';
import { PhaseTab } from './PhaseTab';
import { RewardTab } from './RewardTab';
import { SettlementTab } from './SettlementTab';

export function SectionLeaderboard({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  const { permissions } = usePermissions<PermissionData>();
  const permissionList = permissions?.permissions;
  // POST /blind-box/feature-flags requires
  // bb-leaderboard:update OR bb-activity-basic:update OR bb-holiday-lottery:update
  const canToggleFlag =
    hasPermission(permissionList, 'bb-leaderboard', 'update') ||
    hasPermission(permissionList, 'bb-activity-basic', 'update') ||
    hasPermission(permissionList, 'bb-holiday-lottery', 'update');
  const [tab, setTab] = useState(0);
  const [phases, setPhases] = useState<BbLbPhase[]>([]);
  const [catalog, setCatalog] = useState<BbLbBoardCatalogEntry[]>([]);
  // Module kill-switch (leaderboard_enabled). null = not loaded yet.
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [savingFlag, setSavingFlag] = useState(false);

  const reloadPhases = useCallback(() => {
    bbLbGetPhases()
      .then(setPhases)
      .catch((e: any) => {
        notify(e?.message || 'Error', { type: 'error' });
        setPhases([]);
      });
  }, [notify]);

  // The board catalog is a fixed, phase-independent allow-list — load it once.
  const reloadCatalog = useCallback(() => {
    bbLbGetBoardCatalog()
      .then(setCatalog)
      .catch(() => setCatalog([]));
  }, []);

  const reloadFlag = useCallback(() => {
    bbGetFeatureFlags()
      .then((rows) => setEnabled(!!rows.find((r) => r.key === BB_LEADERBOARD_FLAG)?.value))
      .catch(() => setEnabled(null));
  }, []);

  useEffect(() => {
    reloadPhases();
    reloadCatalog();
    reloadFlag();
  }, [reloadPhases, reloadCatalog, reloadFlag, refreshKey]);

  const toggleEnabled = async (next: boolean) => {
    if (!next && !window.confirm(t('module.blindBox.lbDisableConfirm'))) return;
    setSavingFlag(true);
    try {
      await bbSetFeatureFlag(BB_LEADERBOARD_FLAG, next);
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setEnabled(next);
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setSavingFlag(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1 }}
        >
          <Typography variant="h6">{t('module.blindBox.leaderboard')}</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={!!enabled}
                disabled={enabled === null || savingFlag || !canToggleFlag}
                onChange={(e) => toggleEnabled(e.target.checked)}
              />
            }
            label={t('module.blindBox.lbFeatureFlag')}
          />
        </Stack>

        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('module.blindBox.lbDesc')}
        </Typography>

        {enabled === false ? (
          <Alert severity="warning" variant="outlined" sx={{ mb: 1.5 }}>
            {t('module.blindBox.lbDisabledWarning')}
          </Alert>
        ) : null}

        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}
        >
          <Tab label={t('module.blindBox.lbTabPhases')} />
          <Tab label={t('module.blindBox.lbTabRewards')} />
          <Tab label={t('module.blindBox.lbTabSettlement')} />
        </Tabs>

        <Box hidden={tab !== 0}>
          {tab === 0 ? <PhaseTab phases={phases} reload={reloadPhases} /> : null}
        </Box>
        <Box hidden={tab !== 1}>
          {tab === 1 ? <RewardTab phases={phases} catalog={catalog} /> : null}
        </Box>
        <Box hidden={tab !== 2}>
          {tab === 2 ? <SettlementTab phases={phases} /> : null}
        </Box>
      </CardContent>
    </Card>
  );
}
